import React, { createContext, useContext, useLayoutEffect, useState } from 'react';
import FR from './fr.json';

const Ctx = createContext({ lang: 'en', setLang: () => {} });
export const useLang = () => useContext(Ctx);

const ATTRS = ['placeholder', 'alt', 'aria-label', 'title'];
const SKIP = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'CODE']);
const norm = (s) => s.replace(/\s+/g, ' ').trim();

const UPPER = Object.fromEntries(Object.entries(FR).map(([k, v]) => [k.toUpperCase(), v.toUpperCase()]));

function lookup(key) {
  if (FR[key]) return FR[key];
  if (key === key.toUpperCase() && UPPER[key]) return UPPER[key];
  const nums = key.match(/\d+/g);
  if (nums && nums.length === 1) {
    const tpl = FR[key.replace(/\d+/, '{n}')];
    if (tpl) return tpl.replace('{n}', nums[0]);
  }
  if (key.includes(' | ')) {
    const parts = key.split(' | ');
    if (parts.some((p) => FR[p])) return parts.map((p) => FR[p] || p).join(' | ');
  }
  return null;
}

function translateText(node) {
  const el = node.parentElement;
  if (!el || SKIP.has(el.tagName) || el.closest('[data-i18n-skip]')) return;
  const cur = node.nodeValue;
  const fr = lookup(norm(cur));
  if (!fr) return;
  const v = cur.match(/^\s*/)[0] + fr + cur.match(/\s*$/)[0];
  if (v !== cur) node.nodeValue = v;
}

function translateAttrs(el) {
  for (const a of ATTRS) {
    const cur = el.getAttribute(a);
    const fr = cur && lookup(norm(cur));
    if (fr && fr !== cur) el.setAttribute(a, fr);
  }
  if (el.tagName === 'META' && /description|og:title|og:description/.test(el.getAttribute('name') || el.getAttribute('property') || '')) {
    const c = el.getAttribute('content');
    const fr = c && lookup(norm(c));
    if (fr && fr !== c) el.setAttribute('content', fr);
  }
}

function translateTree(root) {
  if (root.nodeType === 3) return translateText(root);
  if (root.nodeType !== 1) return;
  translateAttrs(root);
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let n;
  while ((n = w.nextNode())) (n.nodeType === 3 ? translateText(n) : translateAttrs(n));
}

function startTranslator() {
  translateTree(document.head);
  translateTree(document.body);
  const obs = new MutationObserver((records) => {
    for (const r of records) {
      if (r.type === 'characterData') translateText(r.target);
      else if (r.type === 'attributes') translateAttrs(r.target);
      else r.addedNodes.forEach(translateTree);
    }
  });
  const opts = { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: [...ATTRS, 'content'] };
  obs.observe(document.head, opts);
  obs.observe(document.body, opts);
  return obs;
}

function detect() {
  const saved = localStorage.getItem('lang');
  if (saved === 'en' || saved === 'fr') return saved;
  return (navigator.language || '').toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detect);

  useLayoutEffect(() => {
    document.documentElement.lang = lang === 'fr' ? 'fr-CA' : 'en-CA';
    if (lang !== 'fr') return undefined;
    const obs = startTranslator();
    return () => obs.disconnect();
  }, [lang]);

  const setLang = (l) => {
    localStorage.setItem('lang', l);
    setLangState(l);
  };

  return (
    <Ctx.Provider value={{ lang, setLang }}>
      <React.Fragment key={lang}>{children}</React.Fragment>
    </Ctx.Provider>
  );
}
